import classNames                from 'classnames';
import * as PropTypes            from 'prop-types';
import * as React                from 'react';
import { useTranslation }        from 'react-i18next';
import { useLocation }           from 'react-router-dom';
import { SessionStorageService } from 'ts-storage-service';

import { useCatalog } from '../../providers/catalog';

import CatalogItem             from './CatalogItem';
import CatalogSortingFilterBtn from './CatalogSortingFilterBtn';
import PopularSlider           from './PopularSlider';
import { UniqueFeature }       from './UniqueFeature';

// BX-76639: the tags that leave no results go to the end (the order inside both groups is kept);
// a checked tag is never counted as disabled, it has to stay reachable to be unchecked
const sortTagsDisabledLast = (tags, selectedValues) => {
  if (!tags) {
    return tags;
  }

  const isDisabled = (tag) => tag.count === 0 && !selectedValues?.[tag.query_key];

  return [...tags.filter((tag) => !isDisabled(tag)), ...tags.filter(isDisabled)];
};

const CatalogGrid = function(props) {
  const location = useLocation();
  const { t }    = useTranslation();

  const {
          initialStateLoaded,
          installationTypes,
          isInterior,
          isButtonsNextToSubtitle,
          items,
          meta,
          popularModels,
          shareButtonVisible,
        } = props;

  const catalog = useCatalog();

  const ss = new SessionStorageService();

  const parametersWrapperRef = React.useRef(null);
  const scrollLeftRef        = React.useRef(0);
  const startXRef            = React.useRef(0);

  const [parameters, setParameters] = React.useState('start');

  const onMouseMove  = React.useCallback((e) => {
    if (!parametersWrapperRef.current) {
      return;
    }

    const x    = e.pageX - parametersWrapperRef.current.offsetLeft;
    const walk = (x - startXRef.current) * 2;

    parametersWrapperRef.current.scrollLeft = scrollLeftRef.current - walk;
  }, []);
  const onMouseUp    = React.useCallback(() => {
    if (!parametersWrapperRef.current) {
      return;
    }

    parametersWrapperRef.current.style.removeProperty('user-select');

    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  }, [onMouseMove]);
  const onMouseDown  = React.useCallback((event) => {
    if (!parametersWrapperRef.current) {
      return;
    }
    startXRef.current     = event.pageX - parametersWrapperRef.current.offsetLeft;
    scrollLeftRef.current = parametersWrapperRef.current.scrollLeft;

    parametersWrapperRef.current.style.userSelect = 'none';

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [onMouseMove, onMouseUp]);
  const onScroll     = React.useCallback(() => {
    const wrapper = parametersWrapperRef.current;

    if (wrapper) {
      const scrollPosition    = wrapper.scrollLeft;
      const maxScrollPosition = wrapper.scrollWidth - wrapper.clientWidth;
      const scrollThreshold   = 10;

      if (scrollPosition >= maxScrollPosition - scrollThreshold) {
        setParameters('end');
      } else if (scrollPosition === 0) {
        setParameters('start');
      } else {
        setParameters('center');
      }
    }
  }, []);
  const scrollToItem = React.useCallback((itemElement) => {
    const parametersWrapperElement = parametersWrapperRef.current;

    if (parametersWrapperElement && itemElement) {
      const itemRect      = itemElement.getBoundingClientRect();
      const wrapperRect   = parametersWrapperElement.getBoundingClientRect();
      const itemCenter    = itemRect.left + itemRect.width / 2;
      const wrapperCenter = wrapperRect.left + wrapperRect.width / 2;
      const scrollCenter  = itemCenter - wrapperCenter + parametersWrapperElement.scrollLeft;
      const maxScrollLeft = parametersWrapperElement.scrollWidth - parametersWrapperElement.clientWidth;

      if (scrollCenter >= 0 && scrollCenter <= maxScrollLeft) {
        parametersWrapperElement.scrollTo({
          behavior: 'smooth',
          left:     scrollCenter,
        });
      } else {
        if (itemRect.left + itemRect.width >= wrapperRect.left && itemRect.right + itemRect.width <= wrapperRect.right && itemRect.x + itemRect.width <= wrapperCenter) {
          parametersWrapperElement.scrollTo({
            left:     0,
            behavior: 'smooth',
          });
        } else if (itemRect.left + itemRect.width >= wrapperRect.left && itemRect.right - itemRect.width > wrapperCenter) {
          parametersWrapperElement.scrollTo({
            left:     maxScrollLeft,
            behavior: 'smooth',
          });
        }
      }
    }
  }, []);

  React.useEffect(() => {
    parametersWrapperRef.current?.addEventListener('mousedown', onMouseDown);
    parametersWrapperRef.current?.addEventListener('scroll', onScroll);

    return () => {
      parametersWrapperRef.current?.removeEventListener('mousedown', onMouseDown);
      parametersWrapperRef.current?.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <section className={classNames('grid', { hiddenCatalog: isButtonsNextToSubtitle && !catalog.filter.open })}>
      {isButtonsNextToSubtitle && (
        <section className={classNames('parameters', { open: catalog.filter.open })}>
          <div
            className={classNames('parameters_wrapper', parameters)}
            ref={parametersWrapperRef}
          >
            {sortTagsDisabledLast(catalog.filter.filter.tags, catalog.filter.selectedValues)?.map((button) => {
              return (
                <CatalogSortingFilterBtn
                  button={button}
                  key={button.query_key}
                  onSetFilter={(event) => {
                    const filterExists = catalog.filter.selectedValues?.[button.query_key] ? null : 1;

                    ss.removeData('savedCatalogPage');

                    catalog.setFilterParam(`${location.pathname}${location.search}`, button.query_key, filterExists);

                    scrollToItem(event.currentTarget);
                  }}
                  selectedValues={catalog.filter.selectedValues}
                />
              );
            })}
          </div>
        </section>
      )}
      <section className={'body'}>
        <div className={classNames('catalog_items', { catalog_open: catalog.filter.open })}>
          {Object
            .values(items)
            .map((item, i) => {
              if (item.isUniqueFeature) {
                return (
                  <UniqueFeature
                    key={i}
                    {...item}
                  />
                );
              }

              return (
                <CatalogItem
                  isButtonsNextToSubtitle={isButtonsNextToSubtitle}
                  isInterior={isInterior}
                  key={i}
                  offers={item}
                  orientation={'vertical'}
                  shareButtonVisible={shareButtonVisible}
                  showOffers
                  showTags
                  titleH1={meta.titleH1}
                />
              );
            })
          }
          {Object.keys(items).length === 0 && initialStateLoaded && (
            <section className={'empty'}>
              <h3>{t('door_models_not_found')}</h3>
              <div className={'popular'}>
                <PopularSlider
                  installationTypes={installationTypes}
                  isInterior={isInterior}
                  popularModels={popularModels}
                />
              </div>
            </section>
          )}
        </div>
      </section>
    </section>
  );
};

CatalogGrid.defaultProps = {
  meta:   {},
  schema: false,
};

CatalogGrid.propTypes = {
  count:        PropTypes.number.isRequired,
  countModels:  PropTypes.number.isRequired,
  items:        PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  isInterior:   PropTypes.bool,
  label:        PropTypes.string.isRequired,
  meta:         PropTypes.object,
  showMore:     PropTypes.bool.isRequired,
  showMoreFunc: PropTypes.func.isRequired,
  sort:         PropTypes.func,
  schema:       PropTypes.bool,
};
export default CatalogGrid;